import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Clock, ArrowRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ArticleSeo } from "../components/buying-advice/ArticleSeo";
import { ArticleShareButtons } from "../components/buying-advice/ArticleShareButtons";
import { ArticleCta } from "../components/buying-advice/ArticleCta";
import {
  ArticleCard,
  CategoryChip,
} from "../components/buying-advice/ArticleCards";
import { fetchArticleBySlug } from "../lib/articlesApi";

/**
 * Single-article page. Long-form editorial layout: max-w-prose column
 * with generous vertical rhythm, serif headings, comfortable line-height.
 */
export default function BuyingAdviceArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const r = await fetchArticleBySlug(slug);
        if (!alive) return;
        setArticle(r.article);
        setRelated(r.related || []);
      } catch (e) {
        if (!alive) return;
        if (e?.response?.status === 404) setNotFound(true);
        else console.warn("article load failed", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  const canonical = useMemo(
    () =>
      typeof window !== "undefined"
        ? `${window.location.origin}/buying-advice/${slug}`
        : `https://shouldibuyit.co.uk/buying-advice/${slug}`,
    [slug]
  );

  const articleJsonLd = useMemo(() => {
    if (!article) return null;
    const origin =
      typeof window !== "undefined"
        ? window.location.origin
        : "https://shouldibuyit.co.uk";
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.meta_description || article.excerpt,
      image: article.og_image
        ? article.og_image.startsWith("http")
          ? article.og_image
          : `${origin}${article.og_image}`
        : undefined,
      datePublished: article.published_at,
      dateModified: article.published_at,
      author: {
        "@type": "Organization",
        name: article.author || "Should I Buy It Team",
      },
      publisher: {
        "@type": "Organization",
        name: "Should I Buy It",
        logo: {
          "@type": "ImageObject",
          url: `${origin}/brand/logo.png`,
        },
      },
      mainEntityOfPage: canonical,
      articleSection: article.category_name,
      keywords: (article.tags || []).join(", "),
    };
  }, [article, canonical]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Header />
        <div className="max-w-3xl mx-auto px-5 py-20">
          <div className="h-4 w-32 bg-secondary rounded animate-pulse mb-6" />
          <div className="h-10 w-full bg-secondary rounded animate-pulse mb-4" />
          <div className="h-10 w-2/3 bg-secondary rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Header />
        <div className="max-w-3xl mx-auto px-5 py-24 text-center">
          <h1 className="text-3xl font-serif mb-3">Article not found</h1>
          <p className="text-muted-foreground mb-6">
            This article may have been renamed or moved.
          </p>
          <Link
            to="/buying-advice"
            className="inline-flex items-center gap-2 text-primary hover:underline"
          >
            <ChevronLeft className="w-4 h-4" /> Back to Buying Advice
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ArticleSeo
        title={`${article.meta_title || article.title} | Should I Buy It`}
        description={article.meta_description || article.excerpt}
        canonicalUrl={canonical}
        ogImage={
          article.og_image?.startsWith("http")
            ? article.og_image
            : article.og_image
            ? `${window.location.origin}${article.og_image}`
            : undefined
        }
        articleJsonLd={articleJsonLd}
        type="article"
      />
      <Header />

      <main className="max-w-3xl mx-auto px-5 pb-24">
        {/* Back link */}
        <div className="pt-6 pb-2">
          <button
            onClick={() => navigate("/buying-advice")}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition"
            data-testid="article-back"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Buying Advice
          </button>
        </div>

        {/* Header */}
        <header className="pt-6 pb-10">
          <div className="mb-5">
            <CategoryChip
              category_slug={article.category_slug}
              category_name={article.category_name}
            />
          </div>
          <h1
            data-testid="article-title"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight mb-6"
          >
            {article.title}
          </h1>
          <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
            {article.excerpt}
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
            <span>{article.author}</span>
            <span>·</span>
            <span>{article.published_at}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {article.reading_time_min} min read
            </span>
          </div>
        </header>

        {/* Hero image */}
        {article.hero_image ? (
          <div className="aspect-[16/9] mb-12 rounded-2xl overflow-hidden bg-secondary">
            <img
              src={article.hero_image}
              alt={article.hero_image_alt || article.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        ) : (
          <div className="aspect-[16/9] mb-12 rounded-2xl bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900" />
        )}

        {/* Body — long-form editorial styling */}
        <article
          data-testid="article-body"
          className="prose prose-neutral dark:prose-invert max-w-none
            prose-headings:font-serif prose-headings:tracking-tight
            prose-h2:text-2xl sm:prose-h2:text-3xl prose-h2:mt-14 prose-h2:mb-5
            prose-h3:text-xl sm:prose-h3:text-2xl prose-h3:mt-10
            prose-p:text-base prose-p:leading-[1.85]
            prose-a:text-primary prose-a:no-underline hover:prose-a:underline
            prose-strong:font-semibold
            prose-blockquote:border-l-primary/60 prose-blockquote:font-normal prose-blockquote:not-italic prose-blockquote:text-muted-foreground
            prose-hr:border-border
            prose-table:text-sm prose-th:font-semibold
            prose-img:rounded-xl"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.body_md}</ReactMarkdown>
        </article>

        {/* Share row */}
        <div className="mt-16 py-6 border-t border-b border-border flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            Share this article
          </div>
          <ArticleShareButtons title={article.title} url={canonical} />
        </div>

        {/* Chat CTA */}
        <ArticleCta />

        {/* Related articles */}
        {related.length > 0 ? (
          <section data-testid="article-related" className="mt-16">
            <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              Related articles
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {related.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
          </section>
        ) : null}

        {/* Back-to-index footer link */}
        <div className="mt-16 text-center">
          <Link
            to="/buying-advice"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition"
          >
            <ChevronLeft className="w-4 h-4" /> Back to all Buying Advice
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
